import { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Calendar, Images, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Projects() {
  const { lang, t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    document.title = `${t('projects.title')} | Orange Onderhoudsbedrijf`;
    axios.get(`${API}/projects`).then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const openProject = (project) => {
    setSelected(project);
    setImgIdx(0);
  };

  return (
    <div className="bg-[#F8FAFC] pt-20">
      {/* Header */}
      <section data-testid="projects-header" className="bg-white border-b border-slate-100 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Portfolio</span>
          <h1 className="text-5xl md:text-6xl font-black text-[#0F172A] mt-3 mb-5">{t('projects.title')}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed">{t('projects.subtitle')}</p>
        </div>
      </section>

      {/* Grid */}
      <section data-testid="projects-grid" className="px-6 md:px-12 lg:px-24 py-16">
        {projects.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Images size={28} className="text-slate-300" />
            </div>
            <p className="text-[#64748B]">{t('projects.noProjects')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                data-testid={`project-${project.id}`}
                onClick={() => openProject(project)}
                className="group bg-white border border-slate-100 rounded-2xl overflow-hidden hover:border-orange-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-56 overflow-hidden bg-slate-100">
                  {project.images?.[0] ? (
                    <img
                      src={project.images[0]}
                      alt={project.title?.[lang]}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=60'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-slate-300 text-sm">Geen foto</span>
                    </div>
                  )}
                  {project.images?.length > 1 && (
                    <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-md">
                      +{project.images.length - 1}
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#0F172A] text-lg mb-2">{project.title?.[lang]}</h3>
                  <p className="text-[#64748B] text-sm line-clamp-2 mb-3">{project.description?.[lang]}</p>
                  <div className="flex items-center gap-4 text-xs text-[#94A3B8]">
                    <span className="flex items-center gap-1">
                      <MapPin size={12} /> {project.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} /> {project.year}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {selected && (
        <div
          data-testid="project-lightbox"
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-2xl overflow-hidden max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {selected.images?.length > 0 ? (
              <div className="relative">
                <img
                  src={selected.images[imgIdx]}
                  alt=""
                  className="w-full h-80 object-cover"
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=60'; }}
                />
                {selected.images.length > 1 && (
                  <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
                    {selected.images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setImgIdx(i)}
                        className={`w-2 h-2 rounded-full transition-all ${i === imgIdx ? 'bg-white scale-125' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : null}
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="font-bold text-[#0F172A] text-2xl">{selected.title?.[lang]}</h2>
                <button onClick={() => setSelected(null)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <X size={20} />
                </button>
              </div>
              <p className="text-[#64748B] leading-relaxed mb-4">{selected.description?.[lang]}</p>
              <div className="flex items-center gap-6 text-sm text-[#64748B]">
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-[#FF5F00]" />
                  {selected.location}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} className="text-[#FF5F00]" />
                  {selected.year}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
